import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Wrench, Plus, Edit, Trash2, Search, Filter, Clock, AlertTriangle,
  CheckCircle, XCircle, Package, History, ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'power_tools', label: 'Power Tools', icon: '⚡' },
  { value: 'hand_tools', label: 'Hand Tools', icon: '🔧' },
  { value: 'safety_equipment', label: 'Safety Equipment', icon: '🦺' },
  { value: 'measuring_tools', label: 'Measuring Tools', icon: '📏' },
  { value: 'other', label: 'Other', icon: '📦' }
];

const STATUS_CONFIG = {
  available: { label: 'Available', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
  checked_out: { label: 'Checked Out', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Clock },
  maintenance: { label: 'Maintenance', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Wrench },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle }
};

const Tools = ({ user }) => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [historyTool, setHistoryTool] = useState(null);
  const [toolHistory, setToolHistory] = useState([]);
  const [overdueTools, setOverdueTools] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    serial_number: '',
    purchase_date: '',
    condition: 'good'
  });

  useEffect(() => {
    fetchTools();
    fetchOverdueTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tools`);
      setTools(response.data);
    } catch (error) {
      toast.error('Failed to fetch tools');
    } finally {
      setLoading(false);
    }
  };

  const fetchOverdueTools = async () => {
    try {
      const response = await axios.get(`${API}/tools/overdue?days=7`);
      setOverdueTools(response.data);
    } catch (error) {
      console.error('Failed to fetch overdue tools');
    }
  };

  const fetchToolHistory = async (toolId) => {
    try {
      const response = await axios.get(`${API}/tools/${toolId}/history`);
      setToolHistory(response.data);
    } catch (error) {
      toast.error('Failed to fetch tool history');
    }
  };

  const handleAddTool = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tools`, formData);
      toast.success('Tool added successfully!');
      setIsAddDialogOpen(false);
      resetForm();
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add tool');
    }
  };

  const handleUpdateTool = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/tools/${editingTool.id}`, formData);
      toast.success('Tool updated successfully!');
      setEditingTool(null);
      resetForm();
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tool');
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (!window.confirm('Are you sure you want to delete this tool?')) return;
    try {
      await axios.delete(`${API}/tools/${toolId}`);
      toast.success('Tool deleted successfully!');
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tool');
    }
  };

  const handleSetMaintenance = async (toolId) => {
    const notes = prompt('Enter maintenance notes:');
    if (!notes) return;
    try {
      await axios.post(`${API}/tools/${toolId}/maintenance`, { notes });
      toast.success('Tool set to maintenance');
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set maintenance');
    }
  };

  const handleReportLost = async (toolId) => {
    const notes = prompt('Enter details about how the tool was lost:');
    if (!notes) return;
    try {
      await axios.post(`${API}/tools/${toolId}/report-lost`, { notes });
      toast.success('Tool reported as lost');
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report lost');
    }
  };

  const handleForceCheckin = async (toolId) => {
    if (!window.confirm('Force check in this tool? This will remove it from the current holder.')) return;
    try {
      await axios.post(`${API}/tools/${toolId}/checkin`, { notes: 'Force checked in by admin' });
      toast.success('Tool checked in successfully');
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in tool');
    }
  };

  const startEditing = (tool) => {
    setFormData({
      name: tool.name,
      description: tool.description || '',
      category: tool.category,
      serial_number: tool.serial_number || '',
      purchase_date: tool.purchase_date || '',
      condition: tool.condition
    });
    setEditingTool(tool);
  };

  const viewHistory = async (tool) => {
    setHistoryTool(tool);
    await fetchToolHistory(tool.id);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'other',
      serial_number: '',
      purchase_date: '',
      condition: 'good'
    });
  };

  // Admin only page
  if (user.role !== 'admin' && user.role !== 'supervisor') {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Wrench className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access the Tool Inventory page.</p>
        </div>
      </div>
    );
  }

  const filteredTools = tools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.serial_number && tool.serial_number.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || tool.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || tool.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const stats = {
    total: tools.length,
    available: tools.filter(t => t.status === 'available').length,
    checked_out: tools.filter(t => t.status === 'checked_out').length,
    maintenance: tools.filter(t => t.status === 'maintenance').length,
    lost: tools.filter(t => t.status === 'lost').length,
    overdue: overdueTools.length
  };

  const getCategoryLabel = (value) => CATEGORIES.find(c => c.value === value)?.label || value;
  const getCategoryIcon = (value) => CATEGORIES.find(c => c.value === value)?.icon || '📦';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20" data-testid="tools-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Package className="w-8 h-8 text-orange-600" />
              Tool Inventory
            </h1>
            <p className="text-gray-600 mt-1">Manage and track your tools</p>
          </div>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Tool
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-xl">Add New Tool</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddTool} className="space-y-4">
                <div>
                  <Label className="text-gray-700">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., DeWalt Drill"
                    className="text-gray-900 bg-white border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Serial Number</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder="Optional"
                    className="text-gray-900 bg-white border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Condition</Label>
                  <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                    <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                    className="text-gray-900 bg-white border-gray-300"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                    Add Tool
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Overdue Alert */}
        {overdueTools.length > 0 && (
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-800">{overdueTools.length} Overdue Tool{overdueTools.length > 1 ? 's' : ''}</h3>
                <p className="text-red-700 text-sm">Tools checked out for more than 7 days</p>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {overdueTools.slice(0, 3).map(tool => (
                <div key={tool.id} className="text-sm text-red-700">
                  <span className="font-medium">{tool.name}</span> - held by {tool.current_holder_name} at {tool.current_project_name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <p className="text-gray-600 text-sm">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white border-2 border-green-200 rounded-lg p-4">
            <p className="text-gray-600 text-sm">Available</p>
            <p className="text-2xl font-bold text-green-600">{stats.available}</p>
          </div>
          <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
            <p className="text-gray-600 text-sm">Checked Out</p>
            <p className="text-2xl font-bold text-blue-600">{stats.checked_out}</p>
          </div>
          <div className="bg-white border-2 border-yellow-200 rounded-lg p-4">
            <p className="text-gray-600 text-sm">Maintenance</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.maintenance}</p>
          </div>
          <div className="bg-white border-2 border-red-200 rounded-lg p-4">
            <p className="text-gray-600 text-sm">Lost</p>
            <p className="text-2xl font-bold text-red-600">{stats.lost}</p>
          </div>
          <div className="bg-white border-2 border-orange-200 rounded-lg p-4">
            <p className="text-gray-600 text-sm">Overdue</p>
            <p className="text-2xl font-bold text-orange-600">{stats.overdue}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by name or serial number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-gray-900 bg-white border-gray-300"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 text-gray-900 bg-white border-gray-300">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="checked_out">Checked Out</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48 text-gray-900 bg-white border-gray-300">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.icon} {cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tools Grid */}
        {filteredTools.length === 0 ? (
          <div className="bg-white border-2 border-gray-200 rounded-lg p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Tools Found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' 
                ? 'Try adjusting your filters.' 
                : 'Add your first tool to get started.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTools.map((tool) => {
              const statusConfig = STATUS_CONFIG[tool.status] || STATUS_CONFIG.available;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div key={tool.id} className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getCategoryIcon(tool.category)}</span>
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">{tool.name}</h3>
                        <span className="text-sm text-gray-500">{getCategoryLabel(tool.category)}</span>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${statusConfig.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.label}
                    </span>
                  </div>

                  {tool.serial_number && (
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">S/N:</span> {tool.serial_number}
                    </p>
                  )}

                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">Condition:</span> {tool.condition.charAt(0).toUpperCase() + tool.condition.slice(1)}
                  </p>

                  {tool.status === 'checked_out' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">Holder:</span> {tool.current_holder_name}
                      </p>
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">Project:</span> {tool.current_project_name}
                      </p>
                      {tool.last_checked_out && (
                        <p className="text-xs text-blue-600 mt-1">
                          Since: {new Date(tool.last_checked_out).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                        </p>
                      )}
                    </div>
                  )}

                  {tool.description && (
                    <p className="text-sm text-gray-500 mb-4">{tool.description}</p>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                    <Button size="sm" variant="outline" onClick={() => viewHistory(tool)}>
                      <History className="w-4 h-4 mr-1" /> History
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => startEditing(tool)}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    {tool.status === 'checked_out' && (
                      <Button size="sm" variant="outline" onClick={() => handleForceCheckin(tool.id)} className="text-blue-600">
                        <CheckCircle className="w-4 h-4 mr-1" /> Force Check In
                      </Button>
                    )}
                    {tool.status === 'available' && (
                      <Button size="sm" variant="outline" onClick={() => handleSetMaintenance(tool.id)} className="text-yellow-600">
                        <Wrench className="w-4 h-4 mr-1" /> Maintenance
                      </Button>
                    )}
                    {tool.status !== 'lost' && (
                      <Button size="sm" variant="outline" onClick={() => handleReportLost(tool.id)} className="text-red-600">
                        <AlertTriangle className="w-4 h-4 mr-1" /> Lost
                      </Button>
                    )}
                    {tool.status !== 'checked_out' && (
                      <Button size="sm" variant="outline" onClick={() => handleDeleteTool(tool.id)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Edit Dialog */}
        {editingTool && (
          <Dialog open={!!editingTool} onOpenChange={() => { setEditingTool(null); resetForm(); }}>
            <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-xl">Edit Tool</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateTool} className="space-y-4">
                <div>
                  <Label className="text-gray-700">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="text-gray-900 bg-white border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Serial Number</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    className="text-gray-900 bg-white border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Condition</Label>
                  <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                    <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="text-gray-900 bg-white border-gray-300"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setEditingTool(null); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                    Save Changes
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* History Dialog */}
        {historyTool && (
          <Dialog open={!!historyTool} onOpenChange={() => setHistoryTool(null)}>
            <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-xl">
                  History: {historyTool.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {toolHistory.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No history available</p>
                ) : (
                  toolHistory.map((entry) => (
                    <div key={entry.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          entry.action === 'checked_out' ? 'bg-blue-100 text-blue-700' :
                          entry.action === 'checked_in' ? 'bg-green-100 text-green-700' :
                          entry.action === 'reported_lost' ? 'bg-red-100 text-red-700' :
                          entry.action === 'maintenance' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {entry.action.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(entry.timestamp).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">
                        By: <span className="font-medium">{entry.user_name}</span>
                      </p>
                      {entry.project_name && (
                        <p className="text-sm text-gray-600">
                          Project: {entry.project_name}
                        </p>
                      )}
                      {entry.notes && (
                        <p className="text-sm text-gray-500 mt-1 italic">{entry.notes}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="flex justify-end pt-4">
                <Button variant="outline" onClick={() => setHistoryTool(null)}>Close</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default Tools;
